import { scrubPostHogErrorEvent } from '@shared/ui/observability/posthog-error-scrub';
import { resetStorageOnVersionChange } from '@shared/ui/storage/version-reset';
import posthog from 'posthog-js';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';

const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
if (posthogToken) {
	posthog.init(posthogToken, {
		api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
		autocapture: false,
		capture_pageview: false,
		capture_pageleave: false,
		disable_persistence: true,
		disable_session_recording: true,
		person_profiles: 'never',
		capture_exceptions: true,
		before_send: scrubPostHogErrorEvent,
		loaded: (client) =>
			client.register({
				service: 'scope',
				environment:
					import.meta.env.VITE_POSTHOG_ENVIRONMENT ?? (import.meta.env.PROD ? 'prod-fe' : 'dev-fe'),
			}),
	});
}

// Dev-only accessibility check: axe-core scans the rendered page and
// logs any WCAG violations to the console. Runs on axe-core directly (works on
// any React version); dynamically imported so it never ships to prod.
if (import.meta.env.DEV) {
	void import('axe-core').then(({ default: axe }) => {
		setTimeout(() => {
			void axe.run(document.body).then((results) => {
				for (const violation of results.violations) {
					console.warn(`[axe] ${violation.id}: ${violation.help}`, violation.helpUrl);
				}
			});
		}, 1000);
	});
}

// Belt to the react-query `buster`'s braces (root.tsx): on a new build, wipe stale
// client storage BEFORE the persisted query cache rehydrates, so an old-shaped cached
// entity can't outlive a deploy and white-screen a component reading a new field
// (the TAS-89 failure mode). Runs first, before hydrateRoot restores the cache.
resetStorageOnVersionChange(__BUILD_ID__);

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<HydratedRouter />
		</StrictMode>,
	);
});
